import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, readJsonResponse } from '../api/client';
import DeviceChainDiagram from '../components/DeviceChainDiagram';
import DeviceSignalHistory from '../components/DeviceSignalHistory';
import DeviceVoltageHistory from '../components/DeviceVoltageHistory';
import LazyMount from '../components/LazyMount';
import DevicePrevEspHistory from '../components/DevicePrevEspHistory';
import DeviceTimeline from '../components/DeviceTimeline';
import DeviceHistorySkeleton from '../components/skeleton/DeviceHistorySkeleton';
import SkeletonBone from '../components/skeleton/SkeletonBone';
import { buildDeviceHistoryPath } from './deviceHistoryPath';

/** Kolibri SKU: WF/NB = radio, K = Kolibri, A/B = board, T = test. */
function kolibriModelLabel(serial) {
  const prefix = String(serial || '').trim().toUpperCase().slice(0, 4);
  const radio = prefix.startsWith('WFK') ? 'Wi-Fi' : prefix.startsWith('NBK') ? 'NB-IoT' : null;
  if (!radio) {
    return null;
  }

  const board = { A: 'плата A', B: 'плата B', T: 'тест' }[prefix[3]] || null;
  return board ? `Колибри ${radio} · ${board}` : `Колибри ${radio}`;
}

/**
 * Device history: connection chain + activation birthday + available lifecycle events.
 * `embedded` renders the same body inside a modal; serial lookup stays in that modal.
 */
export default function DeviceHistoryPage({
  deviceId: deviceIdProp = null,
  serialNumber: serialNumberProp = null,
  embedded = false,
  onDeviceChange = null,
} = {}) {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const deviceId =
    deviceIdProp != null && deviceIdProp !== '' ? String(deviceIdProp) : params.deviceId;
  const serialHint = serialNumberProp != null ? serialNumberProp : searchParams.get('serial');

  const [chain, setChain] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [chainError, setChainError] = useState(null);
  const [timelineError, setTimelineError] = useState(null);
  const [serialDraft, setSerialDraft] = useState(serialHint || '');
  const [lookupError, setLookupError] = useState(null);
  const serialEditedRef = useRef(false);
  const lookupSeqRef = useRef(0);
  const serialSwapRef = useRef(false);
  const [serialSwap, setSerialSwap] = useState(false);
  const [lookupPending, setLookupPending] = useState(false);
  const [sectionsReady, setSectionsReady] = useState({
    signal: true,
    voltage: true,
    radio: true,
  });

  const noteSectionLoading = useCallback((key, loading) => {
    if (!serialSwapRef.current) {
      return;
    }
    setSectionsReady((current) => {
      const ready = !loading;
      if (current[key] === ready) {
        return current;
      }
      return { ...current, [key]: ready };
    });
  }, []);

  const onSignalLoading = useCallback(
    (loading) => noteSectionLoading('signal', loading),
    [noteSectionLoading],
  );
  const onVoltageLoading = useCallback(
    (loading) => noteSectionLoading('voltage', loading),
    [noteSectionLoading],
  );
  const onRadioLoading = useCallback(
    (loading) => noteSectionLoading('radio', loading),
    [noteSectionLoading],
  );

  useEffect(() => {
    if (!deviceId) {
      setIsLoading(false);
      setChainError('Не указан идентификатор устройства');
      return undefined;
    }

    const abortController = new AbortController();

    async function load() {
      setIsLoading(true);
      setChainError(null);
      setTimelineError(null);

      const [chainResult, timelineResult] = await Promise.allSettled([
        (async () => {
          const response = await apiFetch(`/api/devices/${deviceId}/chain`, {
            signal: abortController.signal,
          });
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message || `Ошибка ${response.status}`);
          }
          return readJsonResponse(response, 'Цепочка устройства');
        })(),
        (async () => {
          const response = await apiFetch(`/api/devices/${deviceId}/timeline`, {
            signal: abortController.signal,
          });
          if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            throw new Error(body.message || `Ошибка ${response.status}`);
          }
          return readJsonResponse(response, 'История устройства');
        })(),
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      if (chainResult.status === 'fulfilled') {
        setChain(chainResult.value);
      } else if (chainResult.reason?.name !== 'AbortError') {
        setChain(null);
        setChainError(chainResult.reason?.message || 'Не удалось загрузить цепочку');
      }

      if (timelineResult.status === 'fulfilled') {
        setTimeline(timelineResult.value);
      } else if (timelineResult.reason?.name !== 'AbortError') {
        setTimeline(null);
        setTimelineError(timelineResult.reason?.message || 'Не удалось загрузить историю');
      }

      setIsLoading(false);
    }

    load();
    return () => abortController.abort();
  }, [deviceId]);

  const loadedSerial =
    timeline?.serial_number || chain?.focus_serial_number || serialHint || '';

  useEffect(() => {
    serialEditedRef.current = false;
  }, [deviceId]);

  useEffect(() => {
    if (serialEditedRef.current) {
      return;
    }
    setSerialDraft(loadedSerial);
  }, [loadedSerial]);

  const openSerial = useCallback(
    async (raw) => {
      const serial = String(raw || '').trim();
      if (!serial) {
        setLookupError('Введите серийный номер');
        return;
      }

      if (serial.toUpperCase() === loadedSerial.trim().toUpperCase()) {
        setLookupError(null);
        return;
      }

      const seq = lookupSeqRef.current + 1;
      lookupSeqRef.current = seq;
      setLookupError(null);
      serialSwapRef.current = true;
      setSerialSwap(true);
      setLookupPending(true);
      setSectionsReady({ signal: false, voltage: false, radio: false });

      try {
        const response = await apiFetch(`/api/devices/lookup?serial=${encodeURIComponent(serial)}`);
        const body = await response.json().catch(() => ({}));
        if (seq !== lookupSeqRef.current) {
          return;
        }
        if (!response.ok) {
          throw new Error(body.message || `Ошибка ${response.status}`);
        }

        if (onDeviceChange) {
          serialEditedRef.current = false;
          onDeviceChange(body.device_id, body.serial_number || serial);
        } else {
          const nextPath = buildDeviceHistoryPath(body.device_id, {
            serialNumber: body.serial_number || serial,
          });
          if (nextPath) {
            serialEditedRef.current = false;
            navigate(nextPath);
          }
        }
      } catch (error) {
        if (seq !== lookupSeqRef.current) {
          return;
        }
        serialSwapRef.current = false;
        setSerialSwap(false);
        setLookupError(error.message || 'Не удалось найти устройство');
      } finally {
        if (seq === lookupSeqRef.current) {
          setLookupPending(false);
        }
      }
    },
    [loadedSerial, navigate, onDeviceChange],
  );

  useEffect(() => {
    if (!serialSwap || lookupPending || isLoading) {
      return;
    }
    if (!sectionsReady.signal || !sectionsReady.voltage || !sectionsReady.radio) {
      return;
    }
    serialSwapRef.current = false;
    setSerialSwap(false);
  }, [isLoading, lookupPending, sectionsReady, serialSwap]);

  const title =
    timeline?.serial_number ||
    chain?.focus_serial_number ||
    serialHint ||
    (deviceId != null ? `Устройство #${deviceId}` : 'Устройство');

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
      {!embedded && (
      <div>
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) {
              navigate(-1);
              return;
            }
            navigate('/risks');
          }}
          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-900"
        >
          ← Назад
        </button>
      </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/60">
        <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500">История устройства</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          <input
            aria-label="Серийный номер"
            value={serialDraft}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="characters"
            disabled={lookupPending}
            onChange={(event) => {
              serialEditedRef.current = true;
              setLookupError(null);
              setSerialDraft(event.target.value.toUpperCase());
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void openSerial(serialDraft);
              }
            }}
            className="w-full max-w-xs rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xl font-semibold leading-tight text-slate-900 outline-none ring-sky-400/30 focus:border-sky-400 focus:ring-2 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          />
          <button
            type="button"
            aria-label="Поиск"
            disabled={lookupPending}
            onClick={() => void openSerial(serialDraft)}
            className="rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {lookupPending ? 'Поиск…' : 'Поиск'}
          </button>
          {serialSwap ? (
            <SkeletonBone className="h-3 w-36" />
          ) : kolibriModelLabel(loadedSerial || title) ? (
            <p className="text-xs text-slate-500">{kolibriModelLabel(loadedSerial || title)}</p>
          ) : null}
        </div>
        {lookupError ? <p className="mt-1 text-sm text-rose-600 dark:text-rose-300">{lookupError}</p> : null}

        {serialSwap ? <DeviceHistorySkeleton /> : null}
        <div className={serialSwap ? 'hidden' : undefined}>
          <DeviceChainDiagram chain={chain} isLoading={isLoading} error={chainError} />
          <LazyMount label="График сигнала…" minHeight={220}>
            <DeviceSignalHistory
              deviceId={deviceId}
              serialNumber={timeline?.serial_number || chain?.focus_serial_number || serialHint}
              onLoadingChange={onSignalLoading}
            />
          </LazyMount>
          <LazyMount label="График напряжения…" minHeight={220}>
            <DeviceVoltageHistory deviceId={deviceId} onLoadingChange={onVoltageLoading} />
          </LazyMount>
          <LazyMount label="Радио…" minHeight={180}>
            <DevicePrevEspHistory deviceId={deviceId} onLoadingChange={onRadioLoading} />
          </LazyMount>
          <DeviceTimeline timeline={timeline} isLoading={isLoading} error={timelineError} />
        </div>
      </div>
    </div>
  );
}
